import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaystackPayment } from 'react-paystack';
import Swal from 'sweetalert2';
import './talentpayment.css';

const TalentPayment = () => {
    const navigate = useNavigate();
    const [selectedPlan, setSelectedPlan] = useState('professional');
    const [isProcessing, setIsProcessing] = useState(false);
    const [companyData, setCompanyData] = useState(null);
    const [paymentError, setPaymentError] = useState('');

    const plans = {
        starter: {
            name: 'Starter',
            price: 499000,
            features: [
                'Access to talent profiles',
                'Basic search and filtering',
                'Contact up to 5 talents',
                'Email support'
            ]
        },
        professional: {
            name: 'Professional',
            price: 999000,
            features: [
                'Everything in Starter',
                'Advanced search filters',
                'Contact unlimited talents',
                'Priority support',
                'Talent matching service'
            ]
        },
        enterprise: {
            name: 'Enterprise',
            price: 1999000,
            features: [
                'Everything in Professional',
                'Dedicated account manager',
                'Custom talent sourcing',
                'API access',
                '24/7 phone support'
            ]
        }
    };

    // Load company data from localStorage
    useEffect(() => {
        const savedCompanyData = localStorage.getItem('companyRegistrationData');
        if (savedCompanyData) {
            setCompanyData(JSON.parse(savedCompanyData));
        } else {
            // If no company data, redirect to registration
            navigate('/company-register');
        }
    }, [navigate]);

    const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "pk_live_4dbf686ee1fcdf6cff24e049f6697e6a3ec53e91";

    const handlePaymentSuccess = async (reference) => {
        setIsProcessing(false);
        setPaymentError('');
        
        try {
            // Verify payment with live server backend
            const response = await fetch('https://server.abiatechhub.com/verify-talent-payment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    reference: reference.reference,
                    amount: plans[selectedPlan].price,
                    email: companyData.email,
                    plan: selectedPlan,
                    company_data: companyData
                })
            });

            const result = await response.json();

            if (result.success) {
                const paymentData = {
                    reference: reference.reference,
                    plan: selectedPlan,
                    companyData: companyData,
                    amount: plans[selectedPlan].price,
                    status: 'success',
                    subscriptionId: result.data.subscription_id,
                    expiresAt: result.data.expires_at
                };

                // Store payment data in localStorage
                localStorage.setItem('paymentData', JSON.stringify(paymentData));
                
                // Clear company registration data
                localStorage.removeItem('companyRegistrationData');
                localStorage.removeItem('selectedTalent');
                
                // Show success message like the existing system
                Swal.fire({
                    title: 'Payment Successful!',
                    text: 'Your account has been activated. You now have access to our talent pool.',
                    icon: 'success',
                    confirmButtonColor: '#8A038C',
                    confirmButtonText: '<i class="fa fa-thumbs-up"></i> Access Dashboard!',
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate('/company-dashboard', { 
                            state: { 
                                plan: selectedPlan, 
                                reference: reference.reference,
                                message: 'Payment successful! Your account has been activated.',
                                paymentData: paymentData
                            }
                        });
                    }
                });
            } else {
                setPaymentError(result.message || 'Payment verification failed. Please contact support.');
            }
        } catch (error) {
            console.error('Payment verification error:', error);
            setPaymentError('Payment verification failed. Please contact support.');
        }
    };

    const handlePaymentClose = () => {
        setIsProcessing(false);
        Swal.fire({
            title: 'Payment Cancelled',
            text: 'You can try again anytime.',
            icon: 'info',
            confirmButtonText: 'OK',
        });
    };

    const componentProps = {
        email: companyData?.email || "company@example.com",
        amount: plans[selectedPlan].price * 100, // Convert to kobo
        publicKey: publicKey,
        text: `Pay ₦${plans[selectedPlan].price.toLocaleString()} NGN`,
        onSuccess: handlePaymentSuccess,
        onClose: handlePaymentClose,
        metadata: {
            company_name: companyData?.companyName || '',
            plan: selectedPlan,
            custom_fields: [
                {
                    display_name: "Company Name",
                    variable_name: "company_name",
                    value: companyData?.companyName || ''
                },
                {
                    display_name: "Plan",
                    variable_name: "plan",
                    value: selectedPlan
                }
            ]
        }
    };

    // Initialize Paystack payment using hook (same pattern as existing system)
    const initializePayment = usePaystackPayment({
        reference: "TALENT" + (new Date()).getTime().toString(),
        email: componentProps.email,
        amount: componentProps.amount,
        publicKey: componentProps.publicKey,
        metadata: componentProps.metadata,
        onSuccess: handlePaymentSuccess,
        onClose: handlePaymentClose,
    });

    // Show loading if company data is not loaded
    if (!companyData) {
        return (
            <div className="talent-payment">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-6 text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading your registration data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="talent-payment">
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-lg-10">
                        <div className="payment-header text-center mb-5">
                            <h1 className="display-4 fw-bold mb-3">Choose Your Plan</h1>
                            <p className="lead text-muted">
                                Welcome {companyData.companyName}! Select the plan that best fits your hiring needs and start connecting with top talent today.
                            </p>
                            {paymentError && (
                                <div className="alert alert-danger" role="alert">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    {paymentError}
                                </div>
                            )}
                        </div>

                        <div className="row g-4">
                            {Object.entries(plans).map(([key, plan]) => (
                                <div className="col-lg-4" key={key}>
                                    <div 
                                        className={`pricing-card ${selectedPlan === key ? 'selected' : ''}`}
                                        onClick={() => setSelectedPlan(key)}
                                    >
                                        {key === 'professional' && (
                                            <div className="pricing-badge">Most Popular</div>
                                        )}
                                        
                                        <div className="pricing-header">
                                            <h4>{plan.name}</h4>
                                            <div className="price">
                                                <span className="currency">₦</span>
                                                <span className="amount">{plan.price.toLocaleString()}</span>
                                                <span className="period">/month</span>
                                            </div>
                                        </div>

                                        <div className="pricing-features">
                                            <ul>
                                                {plan.features.map((feature, index) => (
                                                    <li key={index}>
                                                        <i className="fas fa-check"></i>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="pricing-footer">
                                            {selectedPlan === key ? (
                                                <div className="selected-indicator">
                                                    <i className="fas fa-check-circle"></i>
                                                    Selected
                                                </div>
                                            ) : (
                                                <button 
                                                    className="btn w-100"
                                                    style={{ color: '#8A038C', borderColor: '#8A038C' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedPlan(key);
                                                    }}
                                                >
                                                    Select Plan
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="payment-actions text-center mt-5">
                            <div className="selected-plan-info mb-4">
                                <h4>Selected Plan: {plans[selectedPlan].name}</h4>
                                <p className="text-muted">
                                    You will be charged ₦{plans[selectedPlan].price.toLocaleString()} NGN monthly
                                </p>
                            </div>

                            <button 
                                className="btn btn-lg px-5"
                                onClick={() => {
                                    console.log('Payment button clicked');
                                    console.log('Initializing payment...');
                                    if (initializePayment) {
                                        initializePayment();
                                    } else {
                                        console.log('initializePayment is not available');
                                        Swal.fire({
                                            title: 'Payment Error',
                                            text: 'Payment system is not available. Please try again later.',
                                            icon: 'error',
                                            confirmButtonText: 'OK',
                                        });
                                    }
                                }}
                                style={{ 
                                    backgroundColor: '#8A038C', 
                                    borderColor: '#8A038C', 
                                    color: 'white',
                                    minWidth: '200px',
                                    height: '50px',
                                    fontSize: '16px',
                                    fontWeight: '600'
                                }}
                                disabled={isProcessing}
                            >
                                {isProcessing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </>
                                ) : (
                                    `Pay ₦${plans[selectedPlan].price.toLocaleString()} NGN`
                                )}
                            </button>

                            <div className="payment-security mt-3">
                                <small className="text-muted">
                                    <i className="fas fa-lock me-1"></i>
                                    Secure payment powered by Paystack
                                </small>
                            </div>
                        </div>

                        <div className="payment-benefits mt-5">
                            <div className="row">
                                <div className="col-md-4">
                                    <div className="benefit-item text-center">
                                        <i className="fas fa-shield-alt"></i>
                                        <h5>Secure Payment</h5>
                                        <p>Your payment information is encrypted and secure</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="benefit-item text-center">
                                        <i className="fas fa-clock"></i>
                                        <h5>Instant Access</h5>
                                        <p>Get immediate access to our talent pool after payment</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="benefit-item text-center">
                                        <i className="fas fa-undo"></i>
                                        <h5>Cancel Anytime</h5>
                                        <p>Cancel your subscription at any time with no penalties</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TalentPayment;
